import { useEffect, useRef, useState } from 'react';
import { folderLabel } from 'zitimail-shared-types';
import type {
  Folder,
  MailAddress,
  MessageDetail,
  MessageSummary,
  StorageAccount,
} from 'zitimail-shared-types';

import { api } from '../admin/api';
import { mail } from '../api/mail';
import { uploadToStorage } from '../api/storage';
import {
  ArchiveIcon,
  CloseIcon,
  CodeIcon,
  EnvelopeOpenIcon,
  FilesIcon,
  FlagIcon,
  FolderMoveIcon,
  ForwardIcon,
  PaperclipIcon,
  ReplyAllIcon,
  ReplyIcon,
  ShieldIcon,
  TrashIcon,
  BackIcon,
} from '../components/icons';
import { useToast } from '../components/Toasts';
import { FilePicker, type Crumb } from '../files/Files';
import {
  chineseScript,
  displayName,
  fullDate,
  hue,
  initials,
  size,
  type Conversation,
} from './format';
import { Invitation } from './Invitation';
import { SnoozeButton } from './Snooze';
import { SourceDialog } from './Source';

export type ReplyMode = 'reply' | 'all' | 'forward';
export type ConversationAction =
  'archive' | 'delete' | 'junk' | 'not-junk' | 'unread' | 'flag' | 'unflag';

/** AI Sentry's verdicts, as a reader would say them. */
const SENTRY_VERDICT: Record<string, string> = {
  spam: 'likely spam',
  phishing: 'likely phishing',
  scam: 'likely a scam',
  bec: 'likely impersonation — someone posing as a person you trust',
};

const list = (as: readonly { name?: string; address: string }[]) =>
  as.map((a) => (a.name ? `${a.name} <${a.address}>` : a.address)).join(', ');

/**
 * Attached photos and screenshots, shown under the message as most mail apps
 * do; still listed as files too. Only formats a browser shows as plain
 * pictures, and not huge ones.
 */
const PICTURE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
function pictures(detail: MessageDetail) {
  return detail.attachments.filter(
    (a) => PICTURE_TYPES.has(a.contentType.toLowerCase()) && a.sizeBytes <= 20 * 1024 * 1024,
  );
}

/**
 * The message HTML goes into a sandboxed frame: no script, its own CSP. The
 * BFF already removed anything active; this is the second wall. Same-origin
 * is allowed only so the frame can be sized to its content — with scripts
 * off, nothing inside can use it.
 */
const FRAME_CJK = {
  'zh-Hant': "'PingFang TC','Microsoft JhengHei','Noto Sans CJK TC','Source Han Sans TC'",
  'zh-Hans':
    "'PingFang SC','Hiragino Sans GB','Microsoft YaHei','Noto Sans CJK SC','Source Han Sans SC'",
} as const;

function frameDocument(html: string, lang?: 'zh-Hant' | 'zh-Hans'): string {
  const cjk = lang ? FRAME_CJK[lang] : `${FRAME_CJK['zh-Hans']},${FRAME_CJK['zh-Hant']}`;
  return `<!doctype html><html${lang ? ` lang="${lang}"` : ''}><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: https:; style-src 'unsafe-inline'; font-src data:">
<base target="_blank"><style>html,body{margin:0}body{font:14px/1.55 'IBM Plex Sans',system-ui,${cjk},sans-serif;padding:2px 2px 8px;color:#131820;word-wrap:break-word}img{max-width:100%;height:auto}blockquote{margin:0 0 0 .5em;padding-left:.8em;border-left:3px solid #dde3e7;color:#47525f}</style>
</head><body>${html}</body></html>`;
}

function Body({ detail }: { detail: MessageDetail }): JSX.Element {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(160);
  const lang = chineseScript(`${detail.subject}\n${detail.text ?? detail.html ?? ''}`);
  if (!detail.html) {
    return (
      <pre className="mail-text" lang={lang}>
        {detail.text ?? ''}
      </pre>
    );
  }
  return (
    <iframe
      ref={frame}
      className="mail-body"
      title="Message body"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={frameDocument(detail.html, lang)}
      style={{ height }}
      onLoad={() => {
        const doc = frame.current?.contentDocument;
        if (doc) {
          setHeight(Math.min(Math.max(doc.documentElement.scrollHeight + 8, 80), 20_000));
        }
      }}
    />
  );
}

/** A message's place in the reader: uids repeat across folders. */
const cardKey = (m: MessageSummary) => `${m.folder}\u0000${m.uid}`;

function MessageCard({
  summary,
  detail,
  expanded,
  elsewhere,
  me,
  onExpand,
  onReply,
  onShowImages,
  onChanged,
  onSaveToFiles,
}: {
  summary: MessageSummary;
  detail: MessageDetail | undefined;
  expanded: boolean;
  /** The folder it is in, when that is not the one being read. */
  elsewhere?: string | undefined;
  me: string;
  onExpand: () => void;
  onReply: (detail: MessageDetail, mode: ReplyMode) => void;
  onShowImages: () => void;
  onChanged: () => void;
  /** Saves an attachment to the person's storage (ADR-0049); absent without Files. */
  onSaveToFiles?: ((detail: MessageDetail, part: string, filename: string) => void) | undefined;
}): JSX.Element {
  const sender = summary.from;
  const fromMe = sender.address.toLowerCase() === me.toLowerCase();
  const [showSource, setShowSource] = useState(false);
  if (expanded && !detail) {
    return (
      <article className="msg-card" aria-busy="true">
        <header className="card-head">
          <span
            className="avatar-lg"
            style={{ ['--av-h' as string]: hue(sender.address) }}
            aria-hidden="true"
          >
            {initials(sender)}
          </span>
          <div className="card-from">
            <strong>{fromMe ? 'Me' : displayName(sender)}</strong>
          </div>
          {elsewhere && <span className="card-folder">in {elsewhere}</span>}
          <time className="card-when">{fullDate(summary.date)}</time>
        </header>
        <span className="bone" style={{ width: '80%' }} />
        <span className="bone" style={{ width: '60%' }} />
      </article>
    );
  }
  if (!expanded || !detail) {
    return (
      <button type="button" className="msg-card collapsed" onClick={onExpand}>
        <span
          className="avatar-lg"
          style={{ ['--av-h' as string]: hue(sender.address) }}
          aria-hidden="true"
        >
          {initials(sender)}
        </span>
        <span className="card-who">{fromMe ? 'Me' : displayName(sender)}</span>
        <span className="card-preview">{summary.preview ?? ''}</span>
        {elsewhere && <span className="card-folder">in {elsewhere}</span>}
        <time className="card-when">{fullDate(summary.date)}</time>
      </button>
    );
  }
  return (
    <article className="msg-card">
      <header className="card-head">
        <span
          className="avatar-lg"
          style={{ ['--av-h' as string]: hue(sender.address) }}
          aria-hidden="true"
        >
          {initials(sender)}
        </span>
        <div className="card-from">
          <strong>{displayName(detail.from)}</strong>{' '}
          <span className="addr">&lt;{detail.from.address}&gt;</span>
          <div className="card-to">
            to {list(detail.to)}
            {detail.cc.length > 0 && <> · cc {list(detail.cc)}</>}
          </div>
        </div>
        {elsewhere && <span className="card-folder">in {elsewhere}</span>}
        <time className="card-when">{fullDate(detail.date)}</time>
        <span className="card-actions">
          <button
            type="button"
            className="icon-btn"
            title="Reply (r)"
            aria-label="Reply"
            onClick={() => onReply(detail, 'reply')}
          >
            <ReplyIcon />
          </button>
          <button
            type="button"
            className="icon-btn"
            title="Reply all (a)"
            aria-label="Reply all"
            onClick={() => onReply(detail, 'all')}
          >
            <ReplyAllIcon />
          </button>
          <button
            type="button"
            className="icon-btn"
            title="Forward (f)"
            aria-label="Forward"
            onClick={() => onReply(detail, 'forward')}
          >
            <ForwardIcon />
          </button>
          <button
            type="button"
            className="icon-btn"
            title="View source: the headers and parts as received"
            aria-label="View source"
            onClick={() => setShowSource(true)}
          >
            <CodeIcon />
          </button>
        </span>
      </header>
      {showSource && <SourceDialog detail={detail} onClose={() => setShowSource(false)} />}
      {detail.spam && (
        <div className="verdict warn">
          <ShieldIcon className="verdict-icon" />
          <span>
            <strong>Probably spam.</strong> The spam filter filed this message. Its links and images
            may be unsafe.
          </span>
        </div>
      )}
      {detail.aiVerdict && detail.aiVerdict.verdict !== 'ham' && (
        <div
          className={`verdict ${detail.aiVerdict.verdict === 'spam' ? 'warn' : 'crit'}`}
          role="note"
        >
          <ShieldIcon className="verdict-icon" />
          <span>
            <strong>AI Sentry: {SENTRY_VERDICT[detail.aiVerdict.verdict]}.</strong>{' '}
            {detail.aiVerdict.reason}
          </span>
        </div>
      )}
      <Invitation message={detail} onChanged={onChanged} />
      {detail.blockedImages > 0 && (
        <p className="mail-images">
          {detail.blockedImages} remote image{detail.blockedImages > 1 ? 's' : ''} hidden. Loading
          them tells the sender you opened this.{' '}
          <button type="button" className="link-btn" onClick={onShowImages}>
            Show images
          </button>
        </p>
      )}
      <Body detail={detail} />
      {pictures(detail).length > 0 && (
        <div className="att-pictures" aria-label="Pictures">
          {pictures(detail).map((a) => (
            <a
              key={a.part}
              href={mail.attachmentUrl(detail.folder, detail.uid, a.part)}
              download={a.filename}
              title={`${a.filename} (${size(a.sizeBytes)}) — download`}
            >
              <img
                src={mail.attachmentUrl(detail.folder, detail.uid, a.part, true)}
                alt={a.filename}
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}
      {detail.attachments.length > 0 && (
        <ul className="attachments">
          {detail.attachments.map((a) => (
            <li key={a.part}>
              <a
                href={mail.attachmentUrl(detail.folder, detail.uid, a.part)}
                download={a.filename}
                className="attachment"
              >
                <PaperclipIcon />
                <span className="att-name">{a.filename}</span>
                <span className="att-size">{size(a.sizeBytes)}</span>
              </a>
              {onSaveToFiles && (
                <button
                  type="button"
                  className="icon-btn att-save"
                  title={`Save ${a.filename} to Files`}
                  aria-label={`Save ${a.filename} to Files`}
                  onClick={() => onSaveToFiles(detail, a.part, a.filename)}
                >
                  <FilesIcon />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

export function Reader({
  conversation,
  folders,
  folder,
  me,
  onAction,
  onMove,
  onReply,
  onClose,
  onMakeRule,
  onSnooze,
  banner,
  tenant,
}: {
  conversation: Conversation;
  folders: readonly Folder[];
  folder: string;
  me: string;
  onAction: (action: ConversationAction) => void;
  onMove: (to: string) => void;
  onReply: (detail: MessageDetail, mode: ReplyMode) => void;
  onClose: () => void;
  /** Opens Settings with a rule for mail from this sender. */
  onMakeRule?: ((sender: MailAddress) => void) | undefined;
  /** Snoozes the conversation; absent where snoozing makes no sense. */
  onSnooze?: ((until: Date) => void) | undefined;
  /** Shown above the conversation, e.g. when a scheduled message will go. */
  banner?: JSX.Element | null;
  /** The organisation, for saving attachments to Files. */
  tenant?: string | undefined;
}): JSX.Element {
  const [details, setDetails] = useState<Record<string, MessageDetail>>({});
  const toast = useToast();
  // Saving an attachment to Files: where to, then the upload (scanned again
  // by the API, with today's signatures).
  const [saving, setSaving] = useState<{
    detail: MessageDetail;
    part: string;
    filename: string;
  } | null>(null);
  const saveTo = (account: StorageAccount, folder: Crumb) => {
    const what = saving;
    setSaving(null);
    if (!what) {
      return;
    }
    void (async () => {
      try {
        const response = await fetch(
          mail.attachmentUrl(what.detail.folder, what.detail.uid, what.part),
          {
            credentials: 'include',
          },
        );
        if (!response.ok) {
          throw new Error('the attachment could not be read');
        }
        const blob = await response.blob();
        const saved = await uploadToStorage(
          account.id,
          folder.path,
          blob,
          what.filename,
          () => undefined,
          'attachment',
        ).done;
        toast(
          `${saved.name} saved to ${account.label}${folder.path === '/' ? '' : ` / ${folder.name}`}.`,
        );
      } catch (e) {
        toast(`${what.filename}: ${e instanceof Error ? e.message : 'not saved'}.`, {
          tone: 'error',
        });
      }
    })();
  };
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  // The conversation's parts in other folders: your replies in Sent, say.
  const [related, setRelated] = useState<readonly MessageSummary[]>([]);
  const known = new Set(conversation.messages.flatMap((m) => (m.messageId ? [m.messageId] : [])));
  const oldestFirst = [
    ...conversation.messages,
    ...related.filter((r) => !r.messageId || !known.has(r.messageId)),
  ].sort((a, b) => a.date.localeCompare(b.date));
  const latest = conversation.latest;
  const nameOf = (path: string) =>
    path === 'INBOX' ? 'Inbox' : (folders.find((f) => f.path === path)?.name ?? path);
  const junk = folders.find((f) => f.specialUse === '\\Junk')?.path;
  // The rule is about whoever wrote to me most recently, not my own replies.
  const sender = (
    conversation.messages.find((m) => m.from.address.toLowerCase() !== me.toLowerCase()) ?? latest
  ).from;

  const load = (m: MessageSummary, images = false) =>
    mail
      .message(m.folder, m.uid, images)
      .then((d) => setDetails((all) => ({ ...all, [cardKey(m)]: d })))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Could not open the message.'),
      );

  // The newest message opens; unread ones open too, older read ones fold.
  useEffect(() => {
    const open = new Set(
      conversation.messages.filter((m) => m === latest || !m.seen).map((m) => cardKey(m)),
    );
    setExpanded(open);
    setError(null);
    setRelated([]);
    for (const m of conversation.messages) {
      if (open.has(cardKey(m))) {
        void load(m);
      }
    }
    const ids = [
      ...new Set(
        conversation.messages
          .flatMap((m) => [m.messageId, m.inReplyTo])
          .filter((id): id is string => Boolean(id)),
      ),
    ];
    let current = true;
    if (ids.length > 0) {
      mail
        .related(folder, ids)
        .then((found) => current && setRelated(found.messages))
        // Only extra context: the conversation reads without it.
        .catch(() => undefined);
    }
    return () => {
      current = false;
    };
    // A new conversation, not a re-render, resets what is open.
  }, [latest.folder, latest.uid]);

  return (
    <section className="reader" aria-label="Conversation">
      <div className="reader-bar">
        <button
          type="button"
          className="icon-btn mobile-only"
          title="Back"
          aria-label="Back to the list"
          onClick={onClose}
        >
          <BackIcon />
        </button>
        <button
          type="button"
          className="icon-btn"
          title="Archive (e)"
          aria-label="Archive"
          onClick={() => onAction('archive')}
        >
          <ArchiveIcon />
        </button>
        <button
          type="button"
          className="icon-btn"
          title="Delete (#)"
          aria-label="Delete"
          onClick={() => onAction('delete')}
        >
          <TrashIcon />
        </button>
        {folder === junk ? (
          <button type="button" className="text-btn" onClick={() => onAction('not-junk')}>
            Not junk
          </button>
        ) : (
          <button
            type="button"
            className="icon-btn"
            title="Junk"
            aria-label="Report as junk"
            onClick={() => onAction('junk')}
          >
            <ShieldIcon />
          </button>
        )}
        <span className="bar-sep" />
        <button
          type="button"
          className="icon-btn"
          title="Mark unread (u)"
          aria-label="Mark unread"
          onClick={() => onAction('unread')}
        >
          <EnvelopeOpenIcon />
        </button>
        <button
          type="button"
          className={`icon-btn${conversation.flagged ? ' on' : ''}`}
          title={conversation.flagged ? 'Unflag (s)' : 'Flag (s)'}
          aria-label={conversation.flagged ? 'Unflag' : 'Flag'}
          onClick={() => onAction(conversation.flagged ? 'unflag' : 'flag')}
        >
          <FlagIcon />
        </button>
        {onSnooze && <SnoozeButton onSnooze={onSnooze} />}
        <label className="move-to" title="Move to folder">
          <FolderMoveIcon />
          <select
            aria-label="Move to folder"
            value=""
            onChange={(e) => e.target.value && onMove(e.target.value)}
          >
            <option value="">Move to…</option>
            {folders
              .filter((f) => f.path !== folder)
              .map((f) => (
                <option key={f.path} value={f.path}>
                  {folderLabel(f, folders)}
                </option>
              ))}
          </select>
        </label>
        {onMakeRule && (
          <button
            type="button"
            className="text-btn"
            title={`Make a rule for mail from ${sender.address}`}
            onClick={() => onMakeRule(sender)}
          >
            Make a rule…
          </button>
        )}
        {tenant && sender.address && (
          <SenderButton
            tenant={tenant}
            address={sender.address}
            list={folder === junk ? 'allowed' : 'blocked'}
            onDone={() => onAction(folder === junk ? 'not-junk' : 'junk')}
          />
        )}
        <span className="spacer" />
        <button
          type="button"
          className="icon-btn"
          title="Close (Esc)"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </div>
      {banner}
      <h2 className="reader-subject" lang={chineseScript(latest.subject)}>
        {latest.subject || '(no subject)'}
      </h2>
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <div className="cards">
        {oldestFirst.map((m) => (
          <MessageCard
            key={cardKey(m)}
            summary={m}
            detail={details[cardKey(m)]}
            expanded={expanded.has(cardKey(m))}
            elsewhere={m.folder !== folder ? nameOf(m.folder) : undefined}
            me={me}
            onSaveToFiles={
              tenant ? (detail, part, filename) => setSaving({ detail, part, filename }) : undefined
            }
            onExpand={() => {
              setExpanded((s) => new Set([...s, cardKey(m)]));
              if (!details[cardKey(m)]) {
                void load(m);
              }
            }}
            onReply={onReply}
            onShowImages={() => void load(m, true)}
            onChanged={() => void load(m)}
          />
        ))}
      </div>
      {saving && tenant && (
        <FilePicker
          slug={tenant}
          title={`Save ${saving.filename} to Files`}
          mode={{ kind: 'folder', action: 'Save here', onPick: saveTo }}
          onClose={() => setSaving(null)}
        />
      )}
    </section>
  );
}

/**
 * Puts the sender on the person's own blocked list (and files the message in
 * Junk) or, reading Junk, on their allowed list (and moves it back).
 */
function SenderButton({
  tenant,
  address,
  list,
  onDone,
}: {
  tenant: string;
  address: string;
  list: 'allowed' | 'blocked';
  onDone: () => void;
}): JSX.Element {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const label = list === 'blocked' ? 'Block sender' : 'Always allow sender';
  return (
    <button
      type="button"
      className="text-btn"
      disabled={busy}
      title={
        list === 'blocked'
          ? `Send future mail from ${address} to Junk`
          : `Never send mail from ${address} to Junk for its spam score`
      }
      onClick={() => {
        setBusy(true);
        api(`/tenants/${encodeURIComponent(tenant)}/my-senders/add`, {
          method: 'POST',
          body: { entry: address, list },
        })
          .then(() => {
            toast(
              list === 'blocked'
                ? `Mail from ${address} will go to Junk.`
                : `Mail from ${address} will stay out of Junk.`,
            );
            onDone();
          })
          .catch(() => toast('Could not update your senders. Try again.'))
          .finally(() => setBusy(false));
      }}
    >
      {label}
    </button>
  );
}
